import React, { FC, useCallback, useState, Fragment, useEffect } from 'react'
import { Form, Formik } from 'formik'

import { useStoreActions, useStoreState } from '@mortgage-pos/ui/store'
import newRelic from '@mortgage-pos/ui/services/newRelic'
import rollbar from '@mortgage-pos/ui/services/rollbar'
import Button, { ButtonVariants } from '@mortgage-pos/ui/components/Button'
import { Modal } from '@mortgage-pos/ui/components/Modal'
import {
  useInitialValues,
  useValidate,
} from '@mortgage-pos/ui/utils/formikHelpers'
import FormQuestion from '@mortgage-pos/ui/components/FormQuestion'
import { deriveAnswersFromFormValues } from '@mortgage-pos/ui/utils/pageHelpers'
import { ssnQuestions } from '@mortgage-pos/data'
import { useNavigate } from 'react-router-dom'
import {
  Routes,
  DisclosureType,
  QuestionSetSlug,
  MessageTypes,
} from '@mortgage-pos/types'
import ShieldIcon from '@mortgage-pos/ui/assets/ShieldIcon.svg'
import ApplicationClient from '@mortgage-pos/ui/services/ApplicationClient'
import { useVariation } from '../services/featureFlags'
import { FEATURE_FLAG_TYPES } from '@mortgage-pos/data'
import { environment } from '@mortgage-pos/ui/env'
import { redirectTo } from '@mortgage-pos/utils'

import { customTextMap } from '../utils/customizationHelpers'
import clsx from 'clsx'

export interface SSNModalProps {
  onClose: () => void
  onSubmit: (areScoresEqual: Boolean) => void
  answerStub?: (answers: Object) => void
  isNTL: boolean
}

const SUCCESS_MESSAGE_DURATION = 2000

export const SSNModal: FC<SSNModalProps> = ({
  onClose = () => {},
  onSubmit = () => {},
  answerStub,
  isNTL,
}) => {
  const navigate = useNavigate()

  const { applicationId, applicationGuid } = useStoreState(
    (state) => state.application
  )
  const { mergedAnswers, showSsnSuccess } = useStoreState(
    (state) => state.answers
  )
  const prebuiltValidations = useStoreState(
    (state) => state.visibleIfConditions
  )

  const { getPrequalMaxPurchasePrice } = useStoreActions(
    (state) => state.propertyInfo
  )

  const { isSsnFlow } = useStoreState((state) => state.questionnaire)
  const { hardPullCredit, compareCreditScores, softPullCredit } =
    useStoreActions((state) => state.application)
  const { answer, next } = useStoreActions((state) => state.questionnaire)
  const { setShowSsnSuccess } = useStoreActions((state) => state.answers)

  const [isLoading, setIsLoading] = useState(false)
  const enableSimpleV1 = useVariation(FEATURE_FLAG_TYPES.ENABLE_SIMPLE_V1)

  const validate = useValidate(ssnQuestions)

  const params = new URLSearchParams(window.location.search)
  const isMiniFlow = params.get('mini-flow') === 'true'

  const handleSubmit = useCallback(
    async (values, formikActions) => {
      try {
        setIsLoading(true)

        const answers = deriveAnswersFromFormValues(
          ssnQuestions,
          { ...mergedAnswers, ...values },
          prebuiltValidations
        )
        answerStub ? answerStub(answers) : await answer(answers)

        // Save credit authorization disclosure
        if (values.termsAndCreditDisclosure) {
          const disclosureText = getCustomizedAgreementCopy(
            `${mergedAnswers?.firstName || ''} ${
              mergedAnswers?.lastName || ''
            }`?.trim(),
            isNTL
          )
          await ApplicationClient.fillDisclosures([
            {
              disclosureType: isNTL
                ? DisclosureType.CreditAuthorization
                : DisclosureType.SoftCreditConsent,
              disclosureText,
            },
          ])
        }

        if (!isNTL) {
          if (isMiniFlow) {
            window.parent.postMessage(
              { name: MessageTypes.MiniFlowTaskCompleted },
              '*'
            )
            return
          } else if (isSsnFlow) {
            setShowSsnSuccess(true)
          } else {
            next(mergedAnswers)
          }
        }

        // Save co-borrower credit authorization disclosure if applicable
        if (values.coBorrowerTermsAndCreditDisclosure) {
          const coBorrowerInfo = mergedAnswers.coBorrowerPersonalInfo?.[0] || {}
          const disclosureText = getCustomizedAgreementCopy(
            `${coBorrowerInfo?.firstName || ''} ${
              coBorrowerInfo?.lastName || ''
            }`?.trim(),
            isNTL
          )
          await ApplicationClient.fillCoBorrowerDisclosures([
            {
              disclosureType: isNTL
                ? DisclosureType.CreditAuthorization
                : DisclosureType.SoftCreditConsent,
              disclosureText,
            },
          ])
        }

        // New feature flag logic for the simple-v1 flow
        if (enableSimpleV1 && !isNTL) {
          // Both of these are currently stubbed behind feature flag
          // TODO: Handle failure cases and actual API calls
          const result = await softPullCredit()
          if (result) {
            await getPrequalMaxPurchasePrice()
          }

          setIsLoading(false)

          // After soft pull, redirect to apply-purchase flow
          return redirectTo(
            `${environment.questionnaireBaseUrl}/application-1003?question-set=${QuestionSetSlug.ApplyPurchase}&application-guid=${applicationGuid}`
          )
        }

        let areScoresEqual
        if (isNTL) {
          newRelic.increment('ntl_rate_selection.ssn_submitted')
          await hardPullCredit()
          areScoresEqual = await compareCreditScores()
        }

        setIsLoading(false)
        onSubmit(areScoresEqual || true)
      } catch (e) {
        setIsLoading(false)
        rollbar.error('SSNModal: Error occurred when submitting SSN', e, {
          applicationId,
        })
        navigate(`/${Routes.APP_1003}/error`)
      }
    },
    [
      mergedAnswers,
      answerStub,
      answer,
      hardPullCredit,
      compareCreditScores,
      softPullCredit,
      onSubmit,
      applicationId,
      applicationGuid,
      navigate,
      enableSimpleV1,
      isNTL,
      isSsnFlow,
      next,
      setShowSsnSuccess,
      isMiniFlow,
    ]
  )

  // Delay the transition to show a success message
  useEffect(() => {
    let timeoutId: NodeJS.Timeout

    if (showSsnSuccess) {
      timeoutId = setTimeout(() => {
        next(mergedAnswers)
      }, SUCCESS_MESSAGE_DURATION)
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    }
  }, [showSsnSuccess, next, mergedAnswers])

  const initialValues = useInitialValues(ssnQuestions)

  return (
    <Modal
      className={clsx('ssn-modal', { 'mini-modal': isMiniFlow })}
      onClose={onClose}
    >
      <header>
        <h1 className="ssn-modal__heading">{getHeaderText(isNTL)}</h1>
      </header>
      {showSsnSuccess ? (
        <SuccessMessage />
      ) : (
        <Formik
          initialValues={initialValues}
          validate={validate}
          onSubmit={handleSubmit}
        >
          {({ values }) => {
            return (
              <div>
                <Form>
                  {ssnQuestions.map((question) => {
                    if (question.questionName.name === 'ssn') {
                      question.subTitle = getCustomizedSubtitle(
                        `${mergedAnswers?.firstName || ''} ${
                          mergedAnswers?.lastName || ''
                        }`?.trim(),
                        question.subTitle,
                        false
                      )
                    }

                    if (question.questionName.name === 'coBorrowerSsn') {
                      const coBorrowerInfo =
                        mergedAnswers.coBorrowerPersonalInfo?.[0] || {}

                      question.subTitle = getCustomizedSubtitle(
                        `${coBorrowerInfo?.firstName || ''} ${
                          coBorrowerInfo?.lastName || ''
                        }`?.trim(),
                        question.subTitle,
                        false
                      )
                    }

                    if (
                      question.questionName.name === 'termsAndCreditDisclosure'
                    ) {
                      question.content = getCustomizedAgreementCopy(
                        `${mergedAnswers?.firstName || ''} ${
                          mergedAnswers?.lastName || ''
                        }`?.trim(),
                        isNTL
                      )
                    }

                    if (
                      mergedAnswers.hasCoBorrower === 'Yes' &&
                      question.questionName.name ===
                        'coBorrowerTermsAndCreditDisclosure'
                    ) {
                      const coBorrowerInfo =
                        mergedAnswers.coBorrowerPersonalInfo?.[0] || {}

                      question.content = getCustomizedAgreementCopy(
                        `${coBorrowerInfo?.firstName || ''} ${
                          coBorrowerInfo?.lastName || ''
                        }`?.trim(),
                        isNTL
                      )
                    }

                    return (
                      <Fragment key={question.questionName.name}>
                        {question.questionName.name ===
                        'termsAndCreditDisclosure' ? (
                          <>
                            <div className="ssn-modal__security-blurb">
                              <img
                                className="ssn-modal__shield-icon"
                                src={ShieldIcon}
                                alt="Shield"
                              />
                              Protected with bank-level security
                            </div>
                            <div className="ssn-modal__disclosure">
                              <p>
                                This is an industry standard practice when
                                applying for a mortgage. If this is an
                                application for joint credit, by clicking the
                                "Continue" button, all borrowers intend to apply
                                for joint credit and the expression is made
                                personally, not having authorized another person
                                to make that authorization for you. If
                                individual borrowers have not made the
                                authorizations above and another person clicks
                                the "Continue" button, that is a fraudulent act
                                and is punishable by law. If you do not agree,
                                do not proceed with the application.
                              </p>
                            </div>
                          </>
                        ) : null}
                        <FormQuestion values={values} question={question} />
                      </Fragment>
                    )
                  })}

                  <Button
                    variant={ButtonVariants.next}
                    content={isLoading ? 'Loading...' : 'Continue'}
                    className="ssn-modal__lock-button"
                    type="submit"
                    disabled={isLoading}
                  />
                  {isNTL ? (
                    <Button
                      variant={ButtonVariants.back}
                      className="ssn-modal__back-button"
                      content="Go back"
                      onClick={onClose}
                      disabled={isLoading}
                      data-testid="back-button"
                    />
                  ) : null}
                </Form>
              </div>
            )
          }}
        </Formik>
      )}
    </Modal>
  )
}

function getCustomizedSubtitle(
  borrowerName: string,
  subTitle: string,
  isCoBorrower: boolean
) {
  return (
    <div>
      <div className="ssn-modal__borrower-name">{borrowerName}</div>
      Social Security Number
    </div>
  )
}

function getCustomizedAgreementCopy(borrowerName: string, isNTL: boolean) {
  if (isNTL) {
    return `
    By checking this box, I, ${borrowerName}, have read this authorization and authorize ${customTextMap(
      'sageFullPartnerName'
    )} to obtain my credit report through a hard credit pull.
  `
  } else {
    return "I agree that by checking the box, I'm giving written instructions allowing Sage Home Loans, under the Fair Credit Reporting Act, to obtain info from my credit report for a soft credit check. I understand that this info will be collected and shared with Sage Home Loans by a consumer reporting company."
  }
}

function getHeaderText(isNTL: boolean) {
  if (isNTL) {
    return 'We need to check your credit to give you the most accurate rates'
  } else {
    return 'Social Security Number Upload'
  }
}

const SuccessMessage = () => {
  const [dots, setDots] = useState('')
  const dotsInterval = (SUCCESS_MESSAGE_DURATION - 100) / 3

  useEffect(() => {
    const interval = setInterval(() => {
      setDots((prev) => (prev.length >= 3 ? '' : prev + '.'))
    }, dotsInterval)

    return () => clearInterval(interval)
  }, [dotsInterval])

  return (
    <div className="ssn-modal__success">
      <h2 className="ssn-modal__success-heading">
        Success!{' '}
        <span role="img" aria-label="Checkmark">
          ✅
        </span>
      </h2>
      <p className="ssn-modal__success-text">
        Your information has been securely submitted.
      </p>
      <p className="ssn-modal__success-text">
        Sending you to your Client Portal{dots}
      </p>
    </div>
  )
}

export default SSNModal
